import React, { useState, useMemo } from "react";
import { Download, Play, Info, Clock, FileVideo, FileAudio, CheckCircle2, XCircle } from "lucide-react";
import { VideoDetail, VideoStream, SelectedStream, DownloadStatus } from "./types";
import { downloadVideo } from "./services/worker-service";

// Helper function to save to Chrome storage
const saveToChromeStorage = async (video: VideoDetail): Promise<void> => {};

interface VideoDetailViewProps {
    video: VideoDetail;
    videoId: string; // Add videoId prop
}

const VideoDetailView: React.FC<VideoDetailViewProps> = ({ video, videoId }) => {
    const [selectedStream, setSelectedStream] = useState<SelectedStream | null>(null);
    const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>({ status: "idle" });

    // Memoize filtered streams
    const { videoStreams, audioStreams } = useMemo(
        () => ({
            videoStreams: video.streams.filter((stream) => stream.type === "video"),
            audioStreams: video.streams.filter((stream) => stream.type === "audio"),
        }),
        [video.streams]
    );

    const handleQualitySelect = (stream: VideoStream, quality: string) => {
        setSelectedStream({ stream, quality });
        setDownloadStatus({ status: "idle" });
    };

    const handleDownload = async () => {
        if (!selectedStream) return;

        setDownloadStatus({ status: "downloading" });

        try {
            // Save to Chrome storage
            await saveToChromeStorage(video);

            // Request download from worker
            const response = await downloadVideo({
                videoId,
                itag: selectedStream.stream.itag,
            });

            if (!response.success || !response.data) {
                throw new Error(response.error || "خطا در دریافت لینک دانلود");
            }

            // Create download link with worker response
            const link = document.createElement("a");
            link.href = response.data.url;
            link.download = response.data.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setDownloadStatus({
                status: "success",
                message: "دانلود با موفقیت انجام شد",
            });
        } catch (error) {
            setDownloadStatus({
                status: "error",
                message: error instanceof Error ? error.message : "خطا در دانلود",
            });
        }
    };

    const renderQualityOption = (stream: VideoStream, quality: string, icon: React.ReactNode) => (
        <label key={stream.itag} className="flex items-center space-x-2 p-2 border rounded hover:bg-gray-50 cursor-pointer">
            <input
                type="radio"
                name="quality"
                value={quality}
                checked={selectedStream?.quality === quality}
                onChange={() => handleQualitySelect(stream, quality)}
                className="form-radio"
            />
            {icon}
            <span>{quality}</span>
        </label>
    );

    return (
        <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-800">{video.title}</h1>
                <div className="flex items-center space-x-2">
                    <Info className="w-5 h-5 text-gray-500" />
                    <Clock className="w-5 h-5 text-gray-500" />
                </div>
            </div>

            {/* Thumbnail */}
            <div className="relative mb-6">
                <img src={video.thumbnailUrl} alt={video.title} className="w-full h-auto rounded-lg" />
                <button className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg opacity-0 hover:opacity-100 transition-opacity">
                    <Play className="w-16 h-16 text-white" />
                </button>
            </div>

            {/* Quality Selection */}
            <div className="mb-6">
                <h2 className="text-lg font-semibold mb-3">انتخاب کیفیت</h2>
                <div className="grid grid-cols-2 gap-4">
                    {/* Video Qualities */}
                    <div>
                        <h3 className="text-sm font-medium mb-2">کیفیت ویدیو</h3>
                        <div className="space-y-2">
                            {videoStreams.map((stream) =>
                                renderQualityOption(
                                    stream,
                                    stream.qualityLabel || "Unknown",
                                    <FileVideo className="w-4 h-4 text-gray-500" />
                                )
                            )}
                        </div>
                    </div>

                    {/* Audio Only */}
                    <div>
                        <h3 className="text-sm font-medium mb-2">فقط صدا</h3>
                        <div className="space-y-2">
                            {audioStreams.map((stream) =>
                                renderQualityOption(stream, "فقط صدا", <FileAudio className="w-4 h-4 text-gray-500" />)
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Download Button */}
            <div className="flex justify-center">
                <button
                    onClick={handleDownload}
                    disabled={!selectedStream || downloadStatus.status === "downloading"}
                    className={`flex items-center space-x-2 px-6 py-3 rounded-lg text-white font-medium
                        ${
                            !selectedStream || downloadStatus.status === "downloading"
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-blue-600 hover:bg-blue-700"
                        }`}>
                    <Download className="w-5 h-5" />
                    <span>{downloadStatus.status === "downloading" ? "در حال دانلود..." : "دانلود"}</span>
                </button>
            </div>

            {/* Status Messages */}
            {downloadStatus.status !== "idle" && (
                <div
                    className={`mt-4 flex items-center justify-center space-x-2 ${
                        downloadStatus.status === "success"
                            ? "text-green-600"
                            : downloadStatus.status === "error"
                            ? "text-red-600"
                            : "text-blue-600"
                    }`}>
                    {downloadStatus.status === "success" ? (
                        <>
                            <CheckCircle2 className="w-5 h-5" />
                            <span>{downloadStatus.message}</span>
                        </>
                    ) : downloadStatus.status === "error" ? (
                        <>
                            <XCircle className="w-5 h-5" />
                            <span>{downloadStatus.message}</span>
                        </>
                    ) : null}
                </div>
            )}
        </div>
    );
};

export default VideoDetailView;
